import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import ContentCardV4 from '../../../common/component/ContentCardV4';
import ContentCardWrapperV4 from '../../../common/component/ContentCardWrapperV4';
import FetchResultWrapper from '../../../common/component/FetchResultWrapper';
import {Page} from '../../../common/constant/page';
import useTypedSelector from '../../../common/hook/useTypedSelector';
import {useFetchInfo} from '../../../common/util/fetch';
import {routeTo} from '../../../common/util/PageRouter';
import {IBigContent} from '../../../commonSub/type';
import {getContentOrdNum} from '../../common/common';
import HomeFooter from '../../common/component/HomeFooter';
import {sendHomeEvent, sendHomeViewImp} from '../../common/tracking';
import FilterButtonV4 from '../component/FilterButtonV4';
import TimeTableBigContentCardV4 from '../component/TimeTableBigContentCardV4';
import {actions, ActionType} from '../state/action';
import {ITimeTableContent, ITimeTablePage} from '../type';

interface IProps {
  page: ITimeTablePage;
}

export default function CompleteContentTableV4({page}: IProps) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [completeTab, genres] = useTypedSelector(state => [
    state.timeTable[page].completeTab,
    state.timeTable.genre[page],
  ]);
  const {bigContent, contents} = completeTab;

  const {selectedKey, list: genreList} = genres;

  const {isLoading} = useFetchInfo(ActionType.FetchCompletedContents, page);

  const handleClickBigContent = ({id, sid}: IBigContent) => {
    routeTo({page: Page.Content, id, sid});
    sendHomeEvent({
      action: 'click',
      actionName: 'content',
      customProps: {
        content_id: id,
        event_layer: 'section',
        ordnum: 1,
      },
    });
  };

  const handleClickContent = ({id, sid}: ITimeTableContent, index: number) => {
    routeTo({page: Page.Content, id, sid});
    sendHomeEvent({
      action: 'click',
      actionName: 'content',
      customProps: {
        content_id: id,
        event_layer: 'section',
        ordnum: getContentOrdNum(index, !!bigContent),
      },
    });
  };

  const handleClickFilterButton = () => {
    sendHomeEvent({
      action: 'click',
      actionName: 'genre_filter',
      customProps: {
        event_layer: 'bottom',
      },
    });
  };

  const onSendViewImpFilterModal = useCallback(() => {
    sendHomeViewImp({
      customProps: {
        event_layer: 'popup',
        popup_type: 'timetable_genre_filter',
      },
      viewImpContents: {
        name: 'popup',
      },
    });
  }, []);

  const handleClickCloseModal = (isDimmedClicked?: boolean) => {
    sendHomeEvent({
      action: 'click',
      actionName: 'popup_cancel',
      customProps: {
        event_layer: typeof isDimmedClicked === 'boolean' ? 'dimmed' : 'popup',
        popup_type: 'timetable_genre_filter',
      },
    });
  };

  const handleClickConfirm = (key: string) => {
    dispatch(actions.setCompletedGenreKey(page, key));
    dispatch(actions.fetchCompletedContents(page));
    sendHomeEvent({
      action: 'click',
      actionName: 'popup_ok',
      customProps: {
        event_layer: 'popup',
        popup_type: 'timetable_genre_filter',
        genre: genreList.find(item => item.key === key)?.genre,
      },
    });
  };

  const isEmpty = !bigContent && contents.length === 0;

  return (
    <>
      <FetchResultWrapper
        showLoading={!completeTab || isLoading}
        showNoItems={isEmpty}
        noItemsProps={{textKey: 'schedule_empty'}}
      >
        <div className="flex flex-col gap-4">
          {bigContent && (
            <TimeTableBigContentCardV4
              index={0}
              content={bigContent}
              onClick={handleClickBigContent}
            />
          )}
          <ContentCardWrapperV4 showEmptyContentCard>
            {contents.map((content, index) => (
              <ContentCardV4
                key={content.id}
                content={content}
                onClick={() => handleClickContent(content, index)}
              />
            ))}
          </ContentCardWrapperV4>
          <FilterButtonV4
            itemList={genreList}
            selectedItemKey={selectedKey}
            bottomButtonIconVariant={'genre'}
            title={t('schedule_genre_select_popup')}
            onSendViewImp={onSendViewImpFilterModal}
            onClick={handleClickFilterButton}
            onClickConfirm={handleClickConfirm}
            onClickCloseModal={handleClickCloseModal}
          />
        </div>
      </FetchResultWrapper>
      <HomeFooter isBottomFixed={isEmpty} />
    </>
  );
}
